/**
 * CSK Demo Hub — slug → lazy demo loader registry.
 *
 * One entry per demo wired into the hub. Each entry pairs the URL slug
 * (the browser path component) with a `React.lazy` import of the demo's
 * `App` component. The hub renders the component when
 * `window.location.pathname` matches the slug.
 *
 * For Phase 1–4 demos the import target is a relative path into a
 * sibling workspace's source tree (e.g.
 * `../../SeamBridgeMonograph/ui/src/App.js`); Vite resolves it through
 * the monorepo-wide `server.fs.allow` widened in
 * `demos/hub/vite.config.ts`.
 *
 * Phase 5 migrates demos into `demos/hub/src/apps/<slug>/`. Those entries
 * import from a relative path under `./apps/`; the lookup helpers and
 * launcher are agnostic about which side of the migration a demo is on.
 *
 * To add a new demo: register it in `demos/demo-catalog.json` (the SSOT),
 * then append a corresponding entry here keyed by the same slug.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import { lazy, type ComponentType } from "react";

export interface HubRoute {
  /** URL path component, matching the catalog entry's slug. */
  readonly slug: string;
  /** Lazy-imported demo `App` component. */
  readonly component: ComponentType;
}

interface HubRouteModule {
  readonly App: ComponentType;
}

interface HubLazyRouteModule {
  readonly default: ComponentType;
}

type HubRouteLoader = () => Promise<HubRouteModule>;

function defineHubRoute(slug: string, loadModule: HubRouteLoader): HubRoute {
  return {
    slug,
    component: lazy(
      async (): Promise<HubLazyRouteModule> => ({
        default: (await loadModule()).App,
      }),
    ),
  };
}

const ROUTE_LOADERS = {
  app: async (): Promise<HubRouteModule> => import("../../app/src/App.js"),
  "diagrams-viewer": async (): Promise<HubRouteModule> =>
    import("../../diagrams-viewer/src/App.js"),
  "diagram-interop-lab": async (): Promise<HubRouteModule> =>
    import("./apps/diagram-interop-lab/App.js"),
  "seam-bridge": async (): Promise<HubRouteModule> =>
    import("./apps/seam-bridge/App.js"),
  "hydra-cut": async (): Promise<HubRouteModule> =>
    import("./apps/hydra-cut/App.js"),
  "attack-surface-convergence": async (): Promise<HubRouteModule> =>
    import("./apps/attack-surface-convergence/App.js"),
  "probabilistic-dbg": async (): Promise<HubRouteModule> =>
    import("./apps/probabilistic-dbg/App.js"),
  "agent-tool-execution": async (): Promise<HubRouteModule> =>
    import("./apps/agent-tool-execution/App.js"),
  "causal-seal": async (): Promise<HubRouteModule> =>
    import("./apps/causal-seal/App.js"),
  "expressivity-ladder": async (): Promise<HubRouteModule> =>
    import("./apps/expressivity-ladder/App.js"),
  "authority-separation-discipline": async (): Promise<HubRouteModule> =>
    import("./apps/authority-separation-discipline/App.js"),
  "multi-agent-composition": async (): Promise<HubRouteModule> =>
    import("./apps/multi-agent-composition/App.js"),
  "compositional-safety": async (): Promise<HubRouteModule> =>
    import("./apps/compositional-safety/App.js"),
  "structural-invariant-safety-guarantees": async (): Promise<HubRouteModule> =>
    import("./apps/structural-invariant-safety-guarantees/App.js"),
  "braided-provenance": async (): Promise<HubRouteModule> =>
    import("./apps/braided-provenance/App.js"),
  "structured-provenance-integrity": async (): Promise<HubRouteModule> =>
    import("./apps/structured-provenance-integrity/App.js"),
  "defense-resource-economics": async (): Promise<HubRouteModule> =>
    import("./apps/defense-resource-economics/App.js"),
  "compilers-and-quantum-mechanics": async (): Promise<HubRouteModule> =>
    import("./apps/compilers-and-quantum-mechanics/App.js"),
  "ai-security-by-construction": async (): Promise<HubRouteModule> =>
    import("./apps/ai-security-by-construction/App.js"),
  "safe-closure-architecture": async (): Promise<HubRouteModule> =>
    import("./apps/safe-closure-architecture/App.js"),
  "consumed-by-construction": async (): Promise<HubRouteModule> =>
    import("./apps/consumed-by-construction/App.js"),
  "invariant-lifecycle-governance": async (): Promise<HubRouteModule> =>
    import("./apps/invariant-lifecycle-governance/App.js"),
  "two-categorical-csk": async (): Promise<HubRouteModule> =>
    import("./apps/two-categorical-csk/App.js"),
  "differential-security": async (): Promise<HubRouteModule> =>
    import("./apps/differential-security/App.js"),
  "governed-k": async (): Promise<HubRouteModule> =>
    import("./apps/governed-k/App.js"),
  "equivalence-does-not-authorize": async (): Promise<HubRouteModule> =>
    import("./apps/equivalence-does-not-authorize/App.js"),
  "cryptographic-immunity-forge-perimeter": async (): Promise<HubRouteModule> =>
    import("./apps/cryptographic-immunity-forge-perimeter/App.js"),
  "agentic-boundary-kernel": async (): Promise<HubRouteModule> =>
    import("./apps/agentic-boundary-kernel/App.js"),
  "adversarial-robustness": async (): Promise<HubRouteModule> =>
    import("./apps/adversarial-robustness/App.js"),
  "matrix-immunity-distillation": async (): Promise<HubRouteModule> =>
    import("./apps/matrix-immunity-distillation/App.js"),
  "sheaf-distributed-provenance": async (): Promise<HubRouteModule> =>
    import("./apps/sheaf-distributed-provenance/App.js"),
  "zero-knowledge-compliance": async (): Promise<HubRouteModule> =>
    import("./apps/zero-knowledge-compliance/App.js"),
  "spectral-data-reach": async (): Promise<HubRouteModule> =>
    import("./apps/spectral-data-reach/App.js"),
  "dual-pair-gap": async (): Promise<HubRouteModule> =>
    import("./apps/dual-pair-gap/App.js"),
  "multi-agent-llm-safety": async (): Promise<HubRouteModule> =>
    import("./apps/multi-agent-llm-safety/App.js"),
} satisfies Record<string, HubRouteLoader>;

export const HUB_ROUTES: readonly HubRoute[] = Object.freeze(
  Object.entries(ROUTE_LOADERS).map(([slug, loadModule]) =>
    defineHubRoute(slug, loadModule),
  ),
);

export const HUB_ROUTE_BY_SLUG: ReadonlyMap<string, HubRoute> = new Map(
  HUB_ROUTES.map((entry) => [entry.slug, entry]),
);

export const HUB_ROUTE_SLUGS: ReadonlySet<string> = new Set(
  HUB_ROUTE_BY_SLUG.keys(),
);

export function routeBySlug(slug: string): HubRoute | undefined {
  return HUB_ROUTE_BY_SLUG.get(slug);
}

export function routesBySlug(): ReadonlyMap<string, HubRoute> {
  return HUB_ROUTE_BY_SLUG;
}
